import { getDateTimeFormated } from '@/components/utils/dates/utils';
import {
  PaidAd,
  PaidAdset,
  PaidCampaign
} from '@/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1';

const METRIC_ORDER = [
  'impressions',
  'reach',
  'clicks',
  'link clicks',
  'spent',
  'conversions',
  'results',
  'ctr',
  'cpm',
  'cpc',
  'cpr',
  'roas',
  'frequency',
  'value'
];

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Build a union of all insight keys from a list
const collectAllKeys = (
  list: Array<Record<string, any> | undefined | null>
): string[] => {
  const s = new Set<string>();
  for (const it of list) {
    if (it && typeof it === 'object') {
      for (const k of Object.keys(it)) s.add(k);
    }
  }
  return Array.from(s);
};

// Create an object with every key preset to '-'
const dashInit = (keys: string[]): Record<string, number | '-'> =>
  keys.reduce(
    (acc, k) => ((acc[k] = '-'), acc),
    {} as Record<string, number | '-'>
  );

// Normalize an insights object so it ALWAYS has all keys,
// using rounded numbers for present values, and '-' if missing.
const normalizeInsights = (
  src: Record<string, any> | undefined | null,
  keys: string[]
): Record<string, number | '-'> => {
  const out = dashInit(keys);
  if (!src || typeof src !== 'object') return out;
  for (const [k, v] of Object.entries(src)) {
    const n = toNum(v);
    if (Number.isFinite(n)) out[k] = round2(n);
  }
  return out;
};

const fmt = (v?: string) =>
  typeof v === 'string' && v.trim() ? getDateTimeFormated(v, true) : v;

type CampaignRaw = PaidCampaign & {
  created_time?: string | null;
  updated_time?: string | null;
  adsets?: (PaidAdset & {
    created_time?: string | null;
    updated_time?: string | null;
    ads?: (PaidAd & {
      created_time?: string | null;
      updated_time?: string | null;
      date_start?: string | null;
      date_stop?: string | null;
    })[];
  })[];
};

export function formatDataset(input: unknown[]): PaidCampaign[] {
  const items = Array.isArray(input) ? input : [];

  // Accept either [{ data: campaign }, ...] or [campaign, ...]
  const campaigns: CampaignRaw[] = items
    .map((it: any) =>
      it && typeof it === 'object' && 'data' in it ? it.data : it
    )
    .filter((c: any): c is CampaignRaw => !!c && typeof c === 'object');

  return campaigns.map((c) => {
    const formattedAdsets =
      (c.adsets ?? []).map((as) => {
        const formattedAds =
          (as.ads ?? []).map((ad) => ({
            ...ad,
            // keep any other ad fields intact; format known date-ish ones
            created_time: fmt((ad as any)?.created_time ?? undefined),
            updated_time: fmt((ad as any)?.updated_time ?? undefined),
            date_start: fmt((ad as any)?.date_start ?? undefined),
            date_stop: fmt((ad as any)?.date_stop ?? undefined)
          })) ?? [];

        return {
          ...as,
          created_time: fmt((as as any)?.created_time ?? undefined),
          updated_time: fmt((as as any)?.updated_time ?? undefined),
          ads: formattedAds
        };
      }) ?? [];

    return {
      ...c,
      start_time: fmt(c?.start_time ?? undefined),
      stop_time: fmt(c?.stop_time ?? undefined),
      created_time: fmt((c as any)?.created_time ?? undefined),
      updated_time: fmt((c as any)?.updated_time ?? undefined),
      adsets: formattedAdsets
    } as unknown as PaidCampaign; // cast if PaidCampaign doesn't include created/updated
  });
}

export type DateRange = {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
};

// Coerce numbers safely (supports "1,234.56" strings)
const toNum = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[, ]+/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const orderMetricKeys = (keys: string[]): string[] => {
  const seen = new Set(keys);
  const preferred = METRIC_ORDER.filter((k) => seen.has(k));
  const rest = keys.filter((k) => !preferred.includes(k)).sort();
  return [...preferred, ...rest];
};

// Raw shapes
type AnyCampaign = {
  adsets?: any[];
  insights?: Record<string, any>;
  [k: string]: any;
};
type RawItem = { data?: AnyCampaign } | AnyCampaign;

export function formatUsingAdsNoFilter<T extends RawItem>(
  raw: T[],
  dropEmpty = false
): T[] {
  return raw;
}

// helpers
// Utilities to build Excel sheets for Ads Analytics

// ===== Types & Options =====
export type TFunc = (key: string, def?: string) => string;

export type BuildOptions = {
  includeSummarySheet?: boolean;
  preferredCampaignKeys?: string[];
  preferredAdsetKeys?: string[];
  preferredAdsKeys?: string[];
  excludeKeys?: string[];
  extraColumnCap?: number;
};

const DEFAULT_OPTS: BuildOptions = {
  includeSummarySheet: true,
  preferredCampaignKeys: [
    'id',
    'name',
    'status',
    'objective',
    'start_time',
    'stop_time'
  ],
  // AdSets: adset id + name, then campaign id + name, then the rest
  preferredAdsetKeys: [
    'id',
    'name',
    'campaign_id',
    'campaign_name',
    'status',
    'start_time',
    'stop_time'
  ],
  // Ads: ad id + name, then adset id + name, then campaign id + name, then the rest
  preferredAdsKeys: [
    'id',
    'name',
    'adset_id',
    'adset_name',
    'campaign_id',
    'campaign_name',
    'status',
    'start_time',
    'stop_time'
  ],
  excludeKeys: ['__typename', 'createdAt', 'updatedAt', '__v', '_id'],
  extraColumnCap: 40
};

// ===== Utils =====
const toNumber = (v: any) => {
  const n = typeof v === 'string' ? Number(v.replace(/[, ]/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeCollection = (maybe: any): any[] =>
  Array.isArray(maybe) ? maybe : Array.isArray(maybe?.data) ? maybe.data : [];

const normalizeKey = (k: string) => k.replace(/\s+/g, '_').toLowerCase();

function getNumberWithAliases(
  obj: Record<string, any>,
  candidates: string[]
): number {
  for (const key of candidates) {
    // direct
    if (obj[key] != null && obj[key] !== '') return toNumber(obj[key]);
    // underscored variant of spaced keys
    if (key.includes(' ')) {
      const us = key.replace(/\s+/g, '_');
      if (obj[us] != null && obj[us] !== '') return toNumber(obj[us]);
    }
    // normalized scan across keys
    const want = normalizeKey(key);
    for (const k of Object.keys(obj)) {
      if (normalizeKey(k) === want) return toNumber(obj[k]);
    }
  }
  return 0;
}

// Discover columns from flattened items; keep preferred order first
function discoverColumns(
  items: Record<string, any>[],
  preferred: string[],
  opts: BuildOptions
): string[] {
  const seen = new Set<string>();
  for (const it of items) {
    for (const key of Object.keys(it)) {
      if (opts.excludeKeys!.includes(key)) continue;
      if (/\[\d+\]/.test(key)) continue; // array-ish paths
      seen.add(key);
    }
  }
  const preferredPresent = preferred.filter((k) => seen.has(k));
  const rest = Array.from(seen).filter((k) => !preferredPresent.includes(k));
  // heuristic: push numeric-ish keys earlier among the rest
  const rank = (s: string) =>
    /(spend|spent|impress|click|ctr|cpc|cpm|conv|revenue|amount|count|rate|value)/i.test(
      s
    )
      ? 1
      : 0;
  rest.sort((a, b) => rank(b) - rank(a));
  const capped = rest.slice(0, opts.extraColumnCap);
  return [...preferredPresent, ...capped];
}

const rowsFrom = (
  items: Record<string, any>[],
  preferred: string[],
  opts: BuildOptions
) => {
  const cols = discoverColumns(items, preferred, opts);
  return items.map((it) => {
    const row: Record<string, any> = {};
    for (const k of cols) row[k] = it[k];
    return row;
  });
};

// ===== Main builder (Summary FIRST) =====
export function buildAdAnalyticsExcelSheetsFromFormatted(
  data: any,
  t: TFunc,
  options: BuildOptions = {}
) {
  const opts = {
    ...DEFAULT_OPTS,
    ...options,
    excludeKeys: [
      ...(DEFAULT_OPTS.excludeKeys ?? []),
      ...(options.excludeKeys ?? [])
    ]
  };

  // 1) Normalize & enrich parent keys (keep shapes identical to table data)
  const campaignsRaw = Array.isArray(data?.campaigns)
    ? data.campaigns
    : normalizeCollection(data);

  const adsetsRaw = campaignsRaw.flatMap((c: any) => {
    const cId = c?.id ?? c?.campaign_id ?? null;
    const cName = c?.name ?? c?.campaign_name ?? null;
    return normalizeCollection(c?.adsets).map((as: any) => ({
      ...as,
      campaign_id: cId,
      campaign_name: cName
    }));
  });

  const adsRaw = campaignsRaw.flatMap((c: any) => {
    const cId = c?.id ?? c?.campaign_id ?? null;
    const cName = c?.name ?? c?.campaign_name ?? null;
    return normalizeCollection(c?.adsets).flatMap((as: any) => {
      const asId = as?.id ?? as?.adset_id ?? null;
      const asName = as?.name ?? as?.adset_name ?? null;
      return normalizeCollection(as?.ads).map((ad: any) => ({
        ...ad,
        adset_id: asId,
        adset_name: asName,
        campaign_id: cId,
        campaign_name: cName
      }));
    });
  });

  // Insight keys mirror the table: just whatever is provided, ordered with a metric preference
  const campaignInsightKeys = orderMetricKeys(
    collectAllKeys(campaignsRaw.map((c) => c?.insights))
  );
  const adsetInsightKeys = orderMetricKeys(
    collectAllKeys(adsetsRaw.map((as) => as?.insights))
  );
  const adInsightKeys = orderMetricKeys(
    collectAllKeys(adsRaw.map((ad) => ad?.insights))
  );

  const normalizeDates = (item: any) => ({
    start_time:
      item?.start_time ?? item?.date_start ?? item?.insights?.date_start ?? '-',
    stop_time:
      item?.stop_time ?? item?.date_stop ?? item?.insights?.date_stop ?? '-'
  });

  const mapInsightRow = (
    base: Record<string, any>,
    insights: Record<string, any> | undefined,
    keys: string[]
  ) => ({
    ...base,
    ...normalizeInsights(insights, keys)
  });

  const campaigns = campaignsRaw.map((c: any) =>
    mapInsightRow(
      {
        id: c?.id ?? '-',
        name: c?.name ?? '-',
        status: c?.status ?? '-',
        objective: c?.objective ?? '-',
        ...normalizeDates(c)
      },
      c?.insights,
      campaignInsightKeys
    )
  );

  const adsets = adsetsRaw.map((as: any) =>
    mapInsightRow(
      {
        id: as?.id ?? '-',
        name: as?.name ?? '-',
        campaign_id: as?.campaign_id ?? '-',
        campaign_name: as?.campaign_name ?? '-',
        status: as?.status ?? '-',
        ...normalizeDates(as)
      },
      as?.insights,
      adsetInsightKeys
    )
  );

  const ads = adsRaw.map((ad: any) =>
    mapInsightRow(
      {
        id: ad?.id ?? '-',
        name: ad?.name ?? '-',
        adset_id: ad?.adset_id ?? '-',
        adset_name: ad?.adset_name ?? '-',
        campaign_id: ad?.campaign_id ?? '-',
        campaign_name: ad?.campaign_name ?? '-',
        status: ad?.status ?? '-',
        ...normalizeDates(ad)
      },
      ad?.insights,
      adInsightKeys
    )
  );

  // 3) Summary FIRST (prefer deepest granularity for totals)
  const sumSourceRaw = adsRaw.length
    ? adsRaw
    : adsetsRaw.length
    ? adsetsRaw
    : campaignsRaw;

  const sumFromInsights = (list: any[], candidates: string[]): number =>
    list.reduce(
      (acc, it) => acc + getNumberWithAliases(it?.insights ?? {}, candidates),
      0
    );

  const totalCampaigns = campaigns.length;
  const totalAdsets = adsets.length;
  const totalAds = ads.length;
  const totalImpressions = sumFromInsights(sumSourceRaw, ['impressions']);
  const totalClicks = sumFromInsights(sumSourceRaw, [
    'clicks',
    'link clicks',
    'link_clicks'
  ]);
  const totalSpend = sumFromInsights(sumSourceRaw, [
    'spend',
    'spent',
    'amount spend',
    'amount_spend'
  ]);

  const sheets: Array<{ sheetName: string; data: any[] }> = [];

  if (opts.includeSummarySheet) {
    sheets.push({
      sheetName: 'Summary',
      data: [
        [t('adsAnalytics.summary', 'Summary')],
        [t('adsAnalytics.totalCampaigns', 'Total campaigns'), totalCampaigns],
        [t('adsAnalytics.totalAdsets', 'Total ad sets'), totalAdsets],
        [t('adsAnalytics.totalAds', 'Total ads'), totalAds],
        [t('adsAnalytics.totalSpend', 'Total Spend'), totalSpend],
        [
          t('adsAnalytics.totalImpressions', 'Total Impressions'),
          totalImpressions
        ],
        [t('adsAnalytics.totalClicks', 'Total Clicks'), totalClicks]
      ]
    });
  }

  const resolvedPrefs: BuildOptions = {
    ...opts,
    preferredCampaignKeys: options.preferredCampaignKeys ?? [
      'name',
      'id',
      'status',
      'objective',
      'start_time',
      'stop_time',
      ...campaignInsightKeys
    ],
    preferredAdsetKeys: options.preferredAdsetKeys ?? [
      'name',
      'id',
      'campaign_name',
      'campaign_id',
      'status',
      'start_time',
      'stop_time',
      ...adsetInsightKeys
    ],
    preferredAdsKeys: options.preferredAdsKeys ?? [
      'name',
      'id',
      'adset_name',
      'adset_id',
      'campaign_name',
      'campaign_id',
      'status',
      'start_time',
      'stop_time',
      ...adInsightKeys
    ]
  };

  // 4) Tabular sheets
  if (campaigns.length) {
    sheets.push({
      sheetName: 'Campaigns',
      data: rowsFrom(
        campaigns,
        resolvedPrefs.preferredCampaignKeys!,
        resolvedPrefs
      )
    });
  }

  if (adsets.length) {
    sheets.push({
      sheetName: 'AdSets',
      data: rowsFrom(adsets, resolvedPrefs.preferredAdsetKeys!, resolvedPrefs)
    });
  }

  if (ads.length) {
    sheets.push({
      sheetName: 'Ads',
      data: rowsFrom(ads, resolvedPrefs.preferredAdsKeys!, resolvedPrefs)
    });
  }

  return sheets;
}
